import { useContext } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Workflow } from "@/components/icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { AdminFocusedSurfaceContext } from "@/features/admin-dashboard/admin-focused-surface-context"
import {
  useFlowCreationPolicyQuery,
  useUpdateFlowCreationPolicyMutation,
} from "@/features/admin-dashboard/hooks/admin-queries"

import { AdminSettingsLoadState } from "./admin-settings-load-state"
import { DelegationModeControl } from "./delegation-mode-control"

/**
 * Admin control for who may create/share flows (Fas 1a).
 * "everyone" (default) → anyone can create. "restricted" → only the
 * workflow_admins group. Everyone can still run existing flows either way.
 */
export function FlowCreationPolicyPanel() {
  const { t } = useTranslation()
  const isFocusedSurface = useContext(AdminFocusedSurfaceContext)
  const { data, isLoading, isFetching, refetch } = useFlowCreationPolicyQuery()
  const update = useUpdateFlowCreationPolicyMutation()

  const restricted = data?.policy === "restricted"

  const handleToggle = async (checked: boolean) => {
    try {
      await update.mutateAsync(checked ? "restricted" : "everyone")
      toast.success(
        t("adminDashboard.flowPolicy.saved", { defaultValue: "Flow creation policy updated" })
      )
    } catch {
      toast.error(
        t("adminDashboard.flowPolicy.saveFailed", { defaultValue: "Could not update policy" })
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {!isFocusedSurface ? (
            <CardTitle className="flex items-center gap-2 text-base">
              <Workflow className="size-4" />
              {t("adminDashboard.flowPolicy.title", { defaultValue: "Flow creation" })}
            </CardTitle>
          ) : null}
          <DelegationModeControl capability="flows" />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading || !data ? (
          <AdminSettingsLoadState
            loading={isLoading}
            retrying={isFetching}
            onRetry={() => void refetch()}
          />
        ) : (
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="grid gap-0.5">
              <Label className="text-sm" htmlFor="flow-creation-policy">
                {t("adminDashboard.flowPolicy.restrictLabel", {
                  defaultValue: "Restrict flow creation to workflow admins",
                })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.flowPolicy.restrictHelp", {
                  defaultValue:
                    "When on, only workflow admins can create and share flows. Everyone can still run existing flows.",
                })}
              </p>
            </div>
            <Switch
              checked={restricted}
              disabled={update.isPending}
              id="flow-creation-policy"
              onCheckedChange={(v) => void handleToggle(v)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
